import api from './api';

export interface Employee {
  id: string;
  userId?: string;
  name: string;
  email: string;
  salary: number;
  remunerationValue?: number;
  currency: string;
  paymentDate: number; // Day of month (1-31)
  contractType?: 'Salaried' | 'Day Rate Employee' | 'Day Rate Associate';
  payFrequency?: 'weekly' | 'bi-weekly' | 'monthly';
  payType?: 'salary' | 'day_rate';
  marginPercentage?: number;
  taxCode?: string;
  taxId?: string;
  socialSecurityNumber?: string;
  jurisdiction?: string;
  phone?: string;
  address?: any;
  bankAccountDetails?: any;
  employmentStartDate?: string;
  createdAt: string;
  updatedAt?: string;
}

export interface Payslip {
  id: string;
  userId?: string;
  employeeId: string;
  payPeriodStart: string;
  payPeriodEnd: string;
  grossSalary: number;
  incomeTax: number;
  socialSecurity: number;
  netSalary: number;
  currency: string;
  createdAt: string;
  employeeName?: string; // Populated when joined with employee
}

export interface CreateEmployeeData {
  name: string;
  email: string;
  salary?: number;
  remunerationValue?: number;
  currency: string;
  paymentDate: number;
  contractType?: 'Salaried' | 'Day Rate Employee' | 'Day Rate Associate';
  payFrequency?: 'weekly' | 'bi-weekly' | 'monthly';
  payType?: 'salary' | 'day_rate';
  marginPercentage?: number;
  taxCode?: string;
  taxId?: string;
  socialSecurityNumber?: string;
  jurisdiction?: string;
  phone?: string;
  address?: any;
  bankAccountDetails?: any;
  employmentStartDate?: string;
}

export interface UpdateEmployeeData extends Partial<CreateEmployeeData> {}

export interface PayrollUser {
  id: string;
  name: string;
  email: string;
  payRateDay?: number;
  taxCode?: string;
  socialSecurityNumber?: string;
  role: string;
}

export interface AddUserToPayrollData {
  userId: string;
  salary: number;
  currency: string;
  paymentDate: number;
}

export interface GeneratePayslipData {
  employeeId: string;
  payPeriodStart: string;
  payPeriodEnd: string;
}

export interface PayslipFilters {
  employeeId?: string;
  startDate?: string;
  endDate?: string;
}

export const payrollApi = {
  // Users (for adding to payroll)
  getUsers: async (): Promise<PayrollUser[]> => {
    const response = await api.get('/payroll/users');
    return response.data.data;
  },

  // Employees
  getEmployees: async (): Promise<Employee[]> => {
    const response = await api.get('/payroll/employees');
    return response.data.data;
  },

  addUserToPayroll: async (data: AddUserToPayrollData): Promise<Employee> => {
    const response = await api.post('/payroll/employees/from-user', data);
    return response.data.data;
  },

  createEmployee: async (data: CreateEmployeeData): Promise<Employee> => {
    const response = await api.post('/payroll/employees', data);
    return response.data.data;
  },

  updateEmployee: async (id: string, data: UpdateEmployeeData): Promise<Employee> => {
    const response = await api.put(`/payroll/employees/${id}`, data);
    return response.data.data;
  },

  // Payslips
  getPayslips: async (filters?: PayslipFilters): Promise<Payslip[]> => {
    const params = new URLSearchParams();
    if (filters?.employeeId) params.append('employeeId', filters.employeeId);
    if (filters?.startDate) params.append('startDate', filters.startDate);
    if (filters?.endDate) params.append('endDate', filters.endDate);
    
    const queryString = params.toString();
    const url = `/payroll/payslips${queryString ? `?${queryString}` : ''}`;
    const response = await api.get(url);
    return response.data.data;
  },

  generatePayslip: async (data: GeneratePayslipData): Promise<Payslip> => {
    const response = await api.post('/payroll/payslips/generate', data);
    return response.data.data;
  },

  getPayslipPDF: async (payslipId: string): Promise<Blob> => {
    const response = await api.get(`/payroll/payslips/${payslipId}/pdf`, {
      responseType: 'blob',
    });
    return response.data;
  },

  downloadPayslipPDF: async (payslipId: string): Promise<Blob> => {
    const response = await api.get(`/payroll/payslips/${payslipId}/pdf`, {
      responseType: 'blob',
    });
    return response.data;
  },
};
